import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Me } from '../auth/use-me';
import { Orb } from '../shell/orb';
import { button, fieldLabel, formError, input } from '../shell/look';
import { workspaceFormSchema } from './workspace-form';
import type { WorkspaceForm } from './workspace-form';
import { useCreateWorkspace } from './use-workspaces';

/**
 * A workspace is one team's sources, people and chats. Shown straight after
 * signup, when the person is in none yet, and from the user menu's "New
 * workspace". Creating one makes them its admin and moves the panel onto it.
 */
export function CreateWorkspace({ me, onCancel }: { me: Me; onCancel?: () => void }): JSX.Element {
  const first = me.workspaces.length === 0;
  const form = useForm<WorkspaceForm>({
    resolver: zodResolver(workspaceFormSchema),
    defaultValues: { personName: first ? me.name : '', name: '' },
  });
  const create = useCreateWorkspace(first, onCancel);

  const errors = form.formState.errors;
  const problem =
    errors.personName?.message ?? errors.name?.message ?? create.error?.message ?? undefined;

  return (
    <div className="bg-bg fixed inset-0 z-[80] grid place-items-center overflow-auto p-6">
      <div className="grid-backdrop [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,black,transparent)]" />
      <div className="animate-rise relative flex w-[min(440px,100%)] flex-col items-center gap-[26px]">
        <div className="flex flex-col items-center gap-3.5 text-center">
          <Orb size={56} />
          <div>
            <div className="text-2xl font-semibold tracking-[-.02em]">
              {first ? 'Set up your workspace' : 'New workspace'}
            </div>
            <div className="text-mute mt-1.5 text-sm leading-normal">
              Your fleet's sources, people and conversations live here. You can invite your team
              next.
            </div>
          </div>
        </div>
        <form
          noValidate
          onSubmit={(event) => void form.handleSubmit((values) => create.mutate(values))(event)}
          className="bg-surf shadow-panel flex w-full flex-col gap-4 rounded-[18px] border p-6"
        >
          {first && (
            <label className={fieldLabel}>
              Your name
              <input {...form.register('personName')} autoComplete="name" className={input} />
            </label>
          )}
          <label className={fieldLabel}>
            Workspace name
            <input
              {...form.register('name')}
              autoFocus
              placeholder="Northline Freight"
              className={input}
            />
          </label>
          {problem !== undefined && (
            <div role="alert" className={formError}>
              {problem}
            </div>
          )}
          <button type="submit" disabled={create.isPending} className={button.primary}>
            {create.isPending ? 'Creating…' : 'Create workspace'}
          </button>
          {onCancel !== undefined && (
            <button type="button" onClick={onCancel} className={button.outline}>
              Cancel
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
